import { throttle } from "lodash";
import { message, Modal } from "ant-design-vue";
import { useModelStore } from "@/store/model";
import { useUserStore } from "@/store/user";
import { h } from "vue";
import SvgIcon from "@/components/SvgIcon/index.vue";
import router from "@/router";
import { useLoginStore } from "@/store/login";
import { TokenExpiryReason, logTokenExpiry, logToken, getToken, isCrossTabTokenCleared, tryAdoptSharedCredential, extractTokenFromRequestConfig } from "@/utils/tokenManager";

import { shouldShowTeamQuotaExceededModal, TEAM_QUOTA_EXCEEDED_TITLE, TEAM_QUOTA_EXCEEDED_CONTENT } from "@/utils/teamQuotaWarning";
import { showModelPermissionDeniedModal } from "@/utils/modelPermissionDenied";
import { clearTokenCookie } from "@/utils";
import { isGuestAccessiblePage } from "./guestPagePolicy";

interface ResponseData {
  code: number;
  msg: string;
}

/** 失效响应附带的请求信息：用于取回该请求实际携带的 token 参与比对换新 */
interface RequestMeta {
  headers?: unknown;
}

/**
 * 根据后端返回的 code 和 msg 判断 token 过期场景
 * 不同的场景用不同的文案 + 日志，方便排查"总是掉线"的问题
 */
function resolveTokenExpiryReason(data: ResponseData): TokenExpiryReason {
  // code 90001 = 其它设备挤占
  // code 90000 = 正常过期 / token 无效
  // 如果后端 msg 包含特定关键词，进一步细分
  const msg = (data.msg || "").toLowerCase();

  if (data.code === 90001) {
    return TokenExpiryReason.CONCURRENT_LOGIN;
  }

  // 默认都归为正常过期
  return TokenExpiryReason.NORMAL_EXPIRY;
}

// 登录失效
const handleDisabled = throttle(
  (data: ResponseData, { useMessage } = { useMessage: true }, requestMeta?: RequestMeta) => {
    const modelStore = useModelStore();
    modelStore.open = false;
    modelStore.resetModalState();

    const userStore = useUserStore();
    // const loginStore = useLoginStore();

    // 未登录态下接口也可能返回 90000（如游客进入创作页拉模型详情）
    // 此时再走 logout 会在开发环境触发 location.reload，形成刷新死循环直至崩溃
    if (!userStore.isLogin) {
      logTokenExpiry(TokenExpiryReason.NORMAL_EXPIRY, {
        code: data.code,
        serverMsg: data.msg,
        trigger: "guest_skip_logout",
        tokenPrefix: (getToken() || "").substring(0, 8),
      });
      return Promise.reject(data);
    }

    // 被动失效先比对共享凭证：失败请求携带的 token（或本页 token）与 Cookie 不一致时，说明其他
    // 标签页/端刚写入新登录态，本页只是持有旧 token——静默换新并刷新本页即可。此时清共享存储/
    // Cookie/广播会把刚登录成功的标签页连坐踢下线（多标签页连环互踢），必须先于登出清理执行。
    if (tryAdoptSharedCredential(extractTokenFromRequestConfig(requestMeta)) === "adopted") {
      logToken("被动失效时共享凭证已更新，静默换新并刷新本页", {
        code: data.code,
        serverMsg: data.msg,
        trigger: "passive_adopt_shared_credential",
        failedTokenPrefix: extractTokenFromRequestConfig(requestMeta).substring(0, 8),
        currentTokenPrefix: (getToken() || "").substring(0, 8),
      });
      window.location.reload();
      return Promise.reject(data);
    }

    // 判断具体场景并记录日志
    const reason = resolveTokenExpiryReason(data);
    logTokenExpiry(reason, {
      code: data.code,
      serverMsg: data.msg,
      userId: userStore.userInfo?.id,
      uniqueId: userStore.userInfo?.uniqueId,
      tokenPrefix: (getToken() || "").substring(0, 8),
    });

    // 游客可访问页面（meta.auth === false，如分享画板 /board/share）上出现 90000，
    // 说明是微信/钉钉等内置浏览器残留的过期 token：清理无效凭证并降级为游客态继续浏览，
    // 而不是整页登出跳转，否则打开分享链接会表现为“加载完不停刷新/跳转”。
    if (isGuestAccessiblePage(router.resolve, window.location.pathname, window.location.search)) {
      logTokenExpiry(reason, {
        code: data.code,
        serverMsg: data.msg,
        trigger: "guest_page_token_expired",
        tokenPrefix: (getToken() || "").substring(0, 8),
      });
      void clearTokenCookie();
      userStore.resetSharedAuthUser();
      return Promise.reject(data);
    }

    if (useMessage) {
      // 根据场景使用不同的 toast 文案
      // 90001 场景有专门的弹窗（handleLoginOccupation），这里不走 toast
      if (reason !== TokenExpiryReason.CONCURRENT_LOGIN) {
        message.error("登录已过期，请重新登录");
      }
      // loginStore.open = true;
    }

    userStore.logout({ useLogout: false, reason });

    return Promise.reject(data);
  },
  3000,
  { trailing: false }
);

// 当前登录是否被其它端挤占
let isOccupation = false;

// 登录已被其它用户占用
const handleLoginOccupation = throttle(
  (data: ResponseData, requestMeta?: RequestMeta) => {
    if (isOccupation) return Promise.reject();

    const userStore = useUserStore();
    if (!userStore.isLogin) return Promise.reject();

    // 判断是否为跨标签页同步被动触发：本标签页 token 已被 storage 事件清除
    const isPassiveTrigger = isCrossTabTokenCleared();

    if (isPassiveTrigger) {
      // 跨标签页被动触发：完全无感，静默记录日志，不弹 toast/模态框，不执行登出
      logTokenExpiry(TokenExpiryReason.STORAGE_CLEARED, {
        code: data.code,
        serverMsg: data.msg,
        uniqueId: userStore.userInfo?.uniqueId,
        trigger: "passive_cross_tab",
      });
      return Promise.reject();
    }

    // 共享 Cookie 已被其他标签页/端更新为新登录态（如同浏览器另一标签页重新登录覆盖了单点 token）时，
    // 静默换新并刷新本页，不弹"账号已在其它地方登录"模态框。
    // 必须用失败请求实际携带的 token 比对：storage 同步可能已把内存刷成新 token，内存比对会误判一致
    if (tryAdoptSharedCredential(extractTokenFromRequestConfig(requestMeta)) === "adopted") {
      logToken("挤占失效时共享凭证已更新，静默换新并刷新本页", {
        code: data.code,
        serverMsg: data.msg,
        trigger: "passive_adopt_shared_credential",
        failedTokenPrefix: extractTokenFromRequestConfig(requestMeta).substring(0, 8),
        tokenPrefix: (getToken() || "").substring(0, 8),
      });
      window.location.reload();
      return Promise.reject();
    }

    // 主动触发：本标签页 API 请求直接返回 90001，弹出模态框
    isOccupation = true;

    logTokenExpiry(TokenExpiryReason.CONCURRENT_LOGIN, {
      code: data.code,
      serverMsg: data.msg,
      uniqueId: userStore.userInfo?.uniqueId,
    });

    handleDisabled(data, { useMessage: false }, requestMeta);

    const modalState = Modal.warning({
      title: "账号登录异常",
      icon: h(SvgIcon, { directory: "others", name: "tip-modal-icon", size: 24, class: "anticon" }),
      content: "您的账号已在其它地方登录，请重新登录。如果非本人操作，建议尽快修改密码。",
      okText: "重新登录",
      okType: "primary",
      wrapClassName: "custom-tip-modal",
      bodyStyle: {
        padding: "16px"
      },
      async onOk() {
        modalState.destroy();

        const loginStore = useLoginStore();
        loginStore.handleDeviceEnv();

        isOccupation = false;
      }
    });

    return Promise.reject();
  },
  3000,
  { trailing: false }
);

// 被移除出团队
const handleRemoveGroup = throttle(
  () => {
    logToken("被移出团队", {});
    message.warn("您已被移出该团队，无法使用团队算力/会员权益，已为您切换至个人版使用");
    const userStore = useUserStore();
    userStore.userInfo.curTeam = false;
    setTimeout(() => {
      router.replace("/");
      setTimeout(() => {
        location.reload();
      }, 200);
    }, 1000);
    return Promise.reject();
  },
  3000,
  { trailing: false }
);

// 团队个人额度已用尽（共享时间戳节流，避免 HTTP 与 WebSocket 同时弹窗）
const handleTeamQuotaExceeded = (data: ResponseData) => {
  if (shouldShowTeamQuotaExceededModal()) {
    Modal.warning({
      title: TEAM_QUOTA_EXCEEDED_TITLE,
      icon: h(SvgIcon, { directory: "others", name: "tip-modal-icon", size: 24, class: "anticon" }),
      content: TEAM_QUOTA_EXCEEDED_CONTENT,
      okText: "我知道了",
      okType: "primary",
      wrapClassName: "custom-tip-modal",
      bodyStyle: { padding: "16px" },
      centered: true,
    });
  }
  return Promise.reject(data);
};

// 登录时，提交的邀请码不可用
const handleInviteCodeDisabled = (data: ResponseData) => {
  logToken("邀请码不可用", { code: data.code, msg: data.msg });
  message.warn(data.msg);
  return data;
};

export const responseCodeHandle = (data: ResponseData, requestMeta?: RequestMeta): any => {
  // 登录时，提交的邀请码不可用
  if ([31001, 31002, 31003].includes(data.code)) {
    return handleInviteCodeDisabled(data);
  }

  switch (data.code) {
    case 90000:
      return handleDisabled(data, { useMessage: true }, requestMeta);
    case 90001:
      return handleLoginOccupation(data, requestMeta);
    case 90002: // 被移除出团队版
      return handleRemoveGroup();
    case 90711:
      return handleTeamQuotaExceeded(data);
    case 90712:
      showModelPermissionDeniedModal();
      return Promise.reject(data);
  }
};
